import api from './api';

/**
 * Request a password reset email
 * @param {string} email - User's email address
 * @returns {Promise} Response data
 */
export const requestPasswordReset = async (email) => {
  try {
    const response = await api.post('/users/auth/forgot-password/', {
      email
    });
    return response.data;
  } catch (error) {
    console.error('Error requesting password reset:', error);
    throw error;
  }
};

/**
 * Reset password with a valid token
 * @param {string} token - Password reset token from URL
 * @param {string} newPassword - New password
 * @param {string} confirmPassword - Confirm new password
 * @returns {Promise} Response data
 */
export const resetPassword = async (token, newPassword, confirmPassword) => {
  try {
    const response = await api.post('/users/auth/reset-password/', {
      token,
      new_password: newPassword,
      confirm_password: confirmPassword
    });
    return response.data;
  } catch (error) {
    console.error('Error resetting password:', error);
    throw error;
  }
};

/**
 * Login with email and password
 * @param {string} email - User's email
 * @param {string} password - User's password
 * @returns {Promise} Response data with user info
 */
export const login = async (email, password) => {
  try {
    const response = await api.post('/users/auth/login/', {
      email,
      password
    });
    return response.data;
  } catch (error) {
    console.error('Error logging in:', error);
    throw error;
  }
};

/**
 * Logout current user
 * @returns {Promise} Response data
 */
export const logout = async () => {
  try {
    const response = await api.post('/users/auth/logout/');
    return response.data;
  } catch (error) {
    console.error('Error logging out:', error);
    throw error;
  }
};

/**
 * Register a new user
 * @param {Object} userData - User registration data
 * @returns {Promise} Response data with user info
 */
export const register = async (userData) => {
  try {
    const response = await api.post('/users/auth/register/', userData);
    return response.data;
  } catch (error) {
    console.error('Error registering user:', error);
    throw error;
  }
};

/**
 * Verify authentication token
 * @returns {Promise} Response data with user info
 */
export const verifyAuth = async () => {
  try {
    const response = await api.get('/users/auth/verify/');
    return response.data;
  } catch (error) {
    console.error('Error verifying auth:', error);
    throw error;
  }
};

/**
 * Confirm a user's email with a 6-digit verification code
 * @param {string} email - The email being verified
 * @param {string} code - The 6-digit code from the email
 * @returns {Promise} Response data with updated user info
 */
export const verifyEmail = async (email, code) => {
  try {
    const response = await api.post('/users/auth/verify-email/', {
      email,
      code
    });
    return response.data;
  } catch (error) {
    console.error('Error verifying email:', error);
    throw error;
  }
};

/**
 * Re-send the signup verification code to an email
 * @param {string} email - The email to send a new code to
 * @returns {Promise} Response data
 */
export const resendVerificationCode = async (email) => {
  try {
    const response = await api.post('/users/auth/resend-code/', {
      email
    });
    return response.data;
  } catch (error) {
    console.error('Error resending verification code:', error);
    throw error;
  }
};

const authService = {
  requestPasswordReset,
  resetPassword,
  login,
  logout,
  register,
  verifyAuth,
  verifyEmail,
  resendVerificationCode
};

export default authService;

